import { PrismaticJoint, PulleyJoint, type Body } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { makeWorld, floor, walls, box, rng } from "./helpers";

/**
 * A pulley joint as a load-balancing machine: two trays hang from a rope slung
 * over fixed pulleys. Loose crates tumble onto one tray; their weight pulls it
 * down and hoists the other tray up. The trays ride vertical sliders so they do
 * not spin, while the crates remain free to tumble. (Matter.js has no pulley joint.)
 */
export const pulley: Demo = {
	id: "pulley",
	title: "Pulley",
	blurb:
		"Loose crates tumble onto one tray of a PulleyJoint machine, loading that side so it drops and hoists the empty tray. The trays are rotation-locked by vertical slider constraints; the crates still spin freely.",
	category: "Joints & Machines",
	build(): Scene {
		const world = makeWorld();
		floor(world, 30);
		walls(world, 13, 26);

		const rand = rng(2026);
		const leftX = -4.5;
		const rightX = 4.5;
		const pulleyY = -12;
		const platformY = -5.4;
		const platformW = 3.2;
		const platformH = 0.35;
		const guideLocalY = platformY - pulleyY;

		const leftGuide = box(world, leftX, pulleyY, 0.15, 0.15, { isStatic: true });
		const rightGuide = box(world, rightX, pulleyY, 0.15, 0.15, { isStatic: true });
		const leftPlatform = box(world, leftX, platformY, platformW, platformH, {
			density: 3,
			friction: 1,
		});
		const rightPlatform = box(world, rightX, platformY, platformW, platformH, {
			density: 3,
			friction: 1,
		});

		for (const [guide, platform] of [
			[leftGuide, leftPlatform],
			[rightGuide, rightPlatform],
		] as const) {
			world.addConstraint(
				new PrismaticJoint(guide, platform, {
					localAnchorA: [0, guideLocalY],
					localAnchorB: [0, 0],
					localAxisA: [0, 1],
					enableLimit: true,
					lowerTranslation: -4.2,
					upperTranslation: 4.8,
				}),
			);
		}

		world.addConstraint(
			new PulleyJoint(leftPlatform, rightPlatform, {
				groundAnchorA: [leftX, pulleyY],
				groundAnchorB: [rightX, pulleyY],
				localAnchorA: [0, -platformH],
				localAnchorB: [0, -platformH],
				ratio: 1,
			}),
		);

		const crates: Body[] = [];
		let spawned = 0;
		let timeUntilNext = 0.45;
		const maxCrates = 9;

		const spawnCrate = (): void => {
			const size = 0.52 + rand() * 0.18;
			const x = leftX + (rand() - 0.5) * 1.7;
			const y = -9.6 - rand() * 1.2;
			crates.push(
				box(world, x, y, size, size, {
					density: 2.2,
					friction: 0.85,
					restitution: 0.05,
					angle: (rand() - 0.5) * 0.7,
					velocity: [(rand() - 0.5) * 0.8, 0],
					angularVelocity: (rand() - 0.5) * 6,
				}),
			);
			spawned++;
		};

		spawnCrate();
		spawnCrate();

		const onBeforeStep = (dt: number): void => {
			leftPlatform.wake();
			rightPlatform.wake();
			timeUntilNext -= dt;
			if (spawned < maxCrates && timeUntilNext <= 0) {
				spawnCrate();
				timeUntilNext = 0.65;
			}
			for (let i = 0; i < crates.length; i++) crates[i]!.wake();
		};

		const draw = (ctx: CanvasRenderingContext2D, cam: Camera): void => {
			ctx.save();
			ctx.lineWidth = 3;
			ctx.strokeStyle = "rgba(220, 230, 255, 0.75)";
			ctx.fillStyle = "rgba(20, 26, 42, 0.9)";
			for (const x of [leftX, rightX]) {
				const sx = cam.worldToScreenX(x);
				const sy = cam.worldToScreenY(pulleyY);
				ctx.beginPath();
				ctx.arc(sx, sy, 0.34 * cam.scale, 0, Math.PI * 2);
				ctx.fill();
				ctx.stroke();
			}
			ctx.restore();
		};

		return {
			world,
			camera: new Camera(0, -6, 25),
			onBeforeStep,
			draw,
			hint: "Crates tumble onto the left tray and pull it down; grab boxes or trays to disturb the pulley.",
		};
	},
};
